import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaArrowLeft, FaFileInvoiceDollar } from 'react-icons/fa';
import useYearSelector from '../../hooks/useYearSelector';
import AnioStep from '../../components/DeclaracionRenta/Wizard/AnioStep';
import DJJGateStep from '../../components/DeclaracionRenta/Wizard/DJJGateStep';
import ModuleRenderer from '../../components/DeclaracionRenta/Wizard/ModuleRenderer';
import { getAvailableRegimens, hasModule } from '../../components/DeclaracionRenta/F22/registry';
import PanelEmpresarioIndividual from '../../components/DeclaracionRenta/EmpresarioIndividual/PanelEmpresarioIndividual';
import { getRenta } from '../../services/declaracionRenta/f22Service';
import { nombreRegimen, claveModuloRenta, esClaveEmpresarioIndividual } from '../../services/regimenesTributarios';
import useRegimenEmpresa from '../../hooks/useRegimenEmpresa';
import { useAuth } from '../../context/AuthContext';
import '../../styles/pages/ServiciosSII/DeclaracionRentaPage.css';

const STEPS = {
    ANIO: 'anio',
    PANEL: 'panel',
    CHECKING: 'checking',
    GATE: 'gate',
    SIN_MODULO: 'sin_modulo',
    MODULE: 'module',
};

/**
 * Declaración de Renta: wizard (Año → módulo). El régimen no se pregunta: es el
 * que tiene la empresa en Datos de la empresa (company.regimen_tributario).
 * Tras elegir el año se busca una renta guardada de ese año; si existe, se va
 * directo al módulo con el régimen con que se guardó, aunque después se haya
 * cambiado el de la empresa, para no esconder lo ya declarado — y se salta el
 * gate de DJJ, porque si hay renta calculada es que ya se prepararon. Si no hay
 * nada guardado se pregunta por las DJJ y se abre el módulo del régimen de la
 * empresa, o se avisa si TAXI aún no lo tiene para ese año. Ver
 * src/components/DeclaracionRenta/F22/registry.js para cómo se registra un
 * año/régimen nuevo.
 *
 * Empresario individual (company.empresario_individual): después del año se muestra
 * su panel de tarjetas, y la tarjeta F22 abre este mismo recorrido (abrirRenta) con
 * los módulos de clave «ei_<régimen>». Cada tipo de empresa solo busca y abre los
 * módulos de su tipo. Con «?anio=2026» en la URL (al volver de una tarjeta del panel)
 * se toma ese año y el empresario individual entra directo a su panel.
 */
const DeclaracionRentaPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const anioUrl = Number(searchParams.get('anio')) || null;
    const { selectedCompany, token } = useAuth();
    const idCompany = selectedCompany?.id_company || selectedCompany?.id;
    const { selectedYear, setSelectedYear, yearOptions } = useYearSelector(anioUrl, 1, 0);
    const { regimen: regimenEmpresa, empresarioIndividual, cargando: cargandoRegimen } = useRegimenEmpresa(idCompany, token);
    const [step, setStep] = useState(STEPS.ANIO);
    const [regimenModulo, setRegimenModulo] = useState(null);

    // Vuelta desde una tarjeta del panel: el empresario individual entra directo al panel del año
    useEffect(() => {
        if (anioUrl && !cargandoRegimen && empresarioIndividual) {
            setStep((actual) => (actual === STEPS.ANIO ? STEPS.PANEL : actual));
        }
    }, [anioUrl, cargandoRegimen, empresarioIndividual]);

    // Clave del módulo que corresponde hoy a la empresa: '14d3' o, si es empresario individual, 'ei_14d3'
    const claveEmpresa = regimenEmpresa ? claveModuloRenta(regimenEmpresa, empresarioIndividual) : null;

    const handleFaltaPreparar = () => {
        navigate(`/declaraciones-juradas?anio=${selectedYear}`);
    };

    const handleAnioContinuar = () => {
        if (!idCompany || !token || cargandoRegimen) return;
        if (empresarioIndividual) {
            setStep(STEPS.PANEL);
            return;
        }
        abrirRenta();
    };

    // Renta guardada del año entre los módulos del tipo de la empresa; si no hay, el
    // módulo de su régimen (o el aviso de que TAXI aún no lo tiene)
    const abrirRenta = async () => {
        setStep(STEPS.CHECKING);
        const claves = getAvailableRegimens(selectedYear)
            .filter((clave) => esClaveEmpresarioIndividual(clave) === empresarioIndividual);
        for (const clave of claves) {
            try {
                const result = await getRenta(idCompany, selectedYear, clave);
                if (result.success) {
                    setRegimenModulo(clave);
                    setStep(STEPS.MODULE);
                    return;
                }
            } catch (err) {
                // sin renta guardada en este régimen: se revisa el siguiente
            }
        }

        setRegimenModulo(claveEmpresa);
        setStep(claveEmpresa && hasModule(selectedYear, claveEmpresa) ? STEPS.GATE : STEPS.SIN_MODULO);
    };

    // El régimen de lo que se muestra: el de la renta abierta o, antes, el de la empresa
    const regimenVisible = step === STEPS.MODULE ? regimenModulo : claveEmpresa;
    const guardadaEnOtroRegimen = step === STEPS.MODULE && claveEmpresa && regimenModulo !== claveEmpresa;

    return (
        <div className="declaracion-renta-page">
            <div className="page-header">
                <div className="header-content">
                    <div className="title-section">
                        <FaFileInvoiceDollar className="page-icon" />
                        <div>
                            <h1>Declaración de Renta {step !== STEPS.ANIO ? `AT ${selectedYear}` : ''}</h1>
                            <p className="page-subtitle">
                                Formulario 22 - Declaración anual de impuesto a la renta
                            </p>
                            {regimenVisible && (
                                <p className="dr-regimen">
                                    Régimen: <strong>{nombreRegimen(regimenVisible)}</strong>
                                    {guardadaEnOtroRegimen
                                        ? ` · es el régimen con que se guardó esta renta; la empresa hoy está en ${nombreRegimen(claveEmpresa)}`
                                        : ' · se cambia en Datos de la empresa'}
                                </p>
                            )}
                        </div>
                    </div>
                    <button className="dr-btn-volver" onClick={() => navigate('/dashboard')}>
                        <FaArrowLeft /> Volver al Dashboard
                    </button>
                </div>
            </div>

            <div className="declarar-renta-container">
                {step === STEPS.ANIO && (
                    <AnioStep
                        selectedYear={selectedYear}
                        onYearChange={setSelectedYear}
                        yearOptions={yearOptions}
                        onContinuar={handleAnioContinuar}
                    />
                )}

                {step === STEPS.PANEL && (
                    <PanelEmpresarioIndividual
                        anioTributario={selectedYear}
                        onAbrirF22={abrirRenta}
                    />
                )}

                {step === STEPS.CHECKING && (
                    <div className="f22-loading">
                        <div className="f22-spinner" />
                        <p>Buscando declaración guardada...</p>
                    </div>
                )}

                {step === STEPS.GATE && (
                    <DJJGateStep
                        anioTributario={selectedYear}
                        onConfirmar={() => setStep(STEPS.MODULE)}
                        onFaltaPreparar={handleFaltaPreparar}
                    />
                )}

                {step === STEPS.SIN_MODULO && (
                    <div className="f22-loading">
                        <p>
                            {claveEmpresa
                                ? `TAXI aún no calcula la renta AT ${selectedYear} del régimen ${nombreRegimen(claveEmpresa)}.`
                                : 'No se pudo leer el régimen tributario de la empresa. Revísalo en Datos de la empresa.'}
                        </p>
                    </div>
                )}

                {step === STEPS.MODULE && (
                    <ModuleRenderer anioTributario={selectedYear} regimen={regimenModulo} />
                )}
            </div>

            <div className="page-footer">
                <p className="footer-info">
                    <strong>Nota:</strong> Las declaraciones de renta deben ser presentadas ante el SII
                    durante el mes de abril de cada año. Asegúrese de contar con toda la documentación
                    respaldatoria antes de enviar.
                </p>
            </div>
        </div>
    );
};

export default DeclaracionRentaPage;
